import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
  TextField,
  Popover,
  Divider,
  Skeleton,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import PieChartOutlineIcon from "@mui/icons-material/PieChartOutline";
import moment from "moment";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import { getRequest, postRequest } from "../../globalhelper/helper";
import TaskIcon from "@mui/icons-material/Task";
import CheckIcon from "@mui/icons-material/Check";
import VerifiedIcon from "@mui/icons-material/Verified";
import ErrorIcon from "@mui/icons-material/Error";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AutoAwesomeMotionIcon from "@mui/icons-material/AutoAwesomeMotion";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LayersIcon from '@mui/icons-material/Layers';
import { useSelector, useDispatch } from "react-redux";
import { setInboxData } from "../../redux/Dashboard/dashboardActions";

// Map status keys to their respective icon components and colors
const STATUS_CONFIG = {
  Received: { icon: AutoAwesomeMotionIcon, color: "#6366f1", label: "Invoices Received" },
  "Processed Invoice": { icon: TaskAltIcon, color: "#10b981", label: "Processed" },
  Validated: { icon: VerifiedIcon, color: "#059669", label: "Validated" },
  Activated: { icon: TaskIcon, color: "#7c3aed", label: "Activated" },
  Integrated: { icon: CheckIcon, color: "#0284c7", label: "Integrated" },
  "Duplicate Invoice": { icon: ContentCopyIcon, color: "#f59e0b", label: "Duplicate" },
  "Not Processed": { icon: PendingActionsIcon, color: "#475569", label: "Not Processed" },
  "Validation Error": { icon: ErrorIcon, color: "#ea580c", label: "Validation Error" },
  "Activation Error": { icon: ContentCopyIcon, color: "#dc2626", label: "Activation Error" },
};

const StatusRow = ({ item, config, isExpandable, onClick, isDialogOpen }) => {
  const Icon = config.icon;
  return (
    <Box sx={{ mb: isDialogOpen ? 0.8 : 0.2 }}>
      <Box
        onClick={isExpandable ? onClick : undefined}
        sx={{
          py: isDialogOpen ? 1.5 : 1.1,
          px: isDialogOpen ? 2 : 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 2,
          border: "1px solid transparent",
          cursor: isExpandable ? "pointer" : "default",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            bgcolor: `${config.color}08`,
            borderColor: `${config.color}15`,
            transform: isExpandable ? "translateX(4px)" : "none",
            boxShadow: isExpandable ? "0 4px 12px rgba(0,0,0,0.03)" : "none"
          }
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: isDialogOpen ? 2 : 1.2, minWidth: 0 }}>
          <Box sx={{
            p: isDialogOpen ? 1 : 0.7,
            borderRadius: 2,
            bgcolor: `${config.color}15`,
            color: config.color,
            display: "flex",
            flexShrink: 0
          }}>
            <Icon sx={{ fontSize: isDialogOpen ? "1.4rem" : "1rem" }} />
          </Box>
          <Typography variant="body2" sx={{
            color: "#475569",
            fontWeight: 700,
            fontSize: isDialogOpen ? "1rem" : "0.85rem",
            letterSpacing: "0.1px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}>
            {config.label}
          </Typography>
          {isExpandable && (
            <ArrowForwardIosIcon sx={{ fontSize: isDialogOpen ? "0.7rem" : "0.55rem", color: "#475569" }} />
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexShrink: 0 }}>
          <Typography variant="h6" sx={{
            fontWeight: 700,
            color: "#475569",
            fontSize: isDialogOpen ? "1.3rem" : "0.9rem",
            letterSpacing: "-0.5px"
          }}>
            {item.value?.toLocaleString() || 0}
          </Typography>
          <Typography variant="caption" sx={{
            color: "#475569",
            fontWeight: 800,
            fontSize: isDialogOpen ? "0.65rem" : "0.52rem",
            textTransform: "uppercase"
          }}>
            UNITS
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

function InboxDashboard({ open, setOpen, company }) {
  const [period, updatePeriod] = useState(moment().format("YYYY-MM-DD"));
  const [companyName, setCompany] = useState(company);
  const [loading, setLoading] = useState(false);

  // Popover State
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverKey, setPopoverKey] = useState(null);

  const dispatch = useDispatch();
  const { companies, inbox } = useSelector((state) => state.dashboard);
  const { status: statusData } = inbox;

  useEffect(() => {
    if (company) {
      setCompany(company);
    } else if (companies?.length === 1 && !companyName) {
      setCompany(companies[0]);
    }
  }, [company, companies, companyName]);

  useEffect(() => {
    if (!companyName?.orgid) return;
    let isMounted = true;
    const fetchData = async () => {
      await getInboxData(() => isMounted);
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [companyName, period]);

  const handleBill = (item) => {
    const orgid = companyName?.orgid;
    if (!orgid) return;

    localStorage.setItem(
      "api",
      `api/edureka/getJSONViaKey?docId=${item.BillNumber}&companyid=${orgid}`,
    );
    localStorage.setItem("selectedItem", JSON.stringify(item));
    localStorage.setItem("orgName", companyName?.orgName || "");

    window.open(
      `/Dynamic_Forms?token=BillExtract&action=edit&orgId=${orgid}&docId=${item.BillNumber}`,
      "_blank",
    );
  };

  const handlePopoverOpen = (event, key) => {
    setAnchorEl(event.currentTarget);
    setPopoverKey(key);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverKey(null);
  };

  const getInboxData = async (getIsMounted) => {
    try {
      setLoading(true);
      const start = moment(period).startOf("month");
      const end = moment(period).endOf("month");

      const days = [];

      let current = start.clone();
      while (current.isSameOrBefore(end)) {
        days.push(current.format("DD-MM-YYYY"));
        current.add(1, "day");
      }
      let res = await postRequest(`api/dashboard/inboxFilter`, {
        item: days,
        companies: companyName.orgid,
      });

      let resultArray = res.res.data;
      let request = {
        orgid: companyName.orgid,
        interimNumbers: resultArray?.map((item) => item.BillNumber),
      };
      const value = await postRequest(
        `api/edureka/getBillTransactionStatus`,
        request,
      );
      let arr1 = resultArray;
      let arr2 = value.res.data;
      const integrationStatus = {
        integrationStatus: "Nil",
      };

      const combinedArray = arr1?.map((item) => {
        if (arr2?.length) {
          const matched = arr2.find(
            (val) => val.BillNumber === item.BillNumber,
          );
          if (matched) {
            return { ...item, ...integrationStatus, ...matched };
          } else {
            return { ...item, ...integrationStatus };
          }
        } else {
          return { ...item };
        }
      }) || [];

      const validatedList = combinedArray.filter((item) => item.Status === "Validated");
      const validationErrorList = combinedArray.filter((item) => item.Status === "Validation Error");
      const activatedList = combinedArray.filter((item) => item.Status === "Activated");
      const activationErrorList = combinedArray.filter((item) => item.Status === "Activation Error");
      const processedList = combinedArray.filter((item) => item.Status === "Processed");
      const notProcessedList = combinedArray.filter((item) => item.Status === "Not Processed");
      const integratedList = combinedArray.filter((item) => item.integrationStatus === "success");

      // Store only serializable data in Redux
      let status = {
        Received: {
          value: combinedArray.length,
          section: "Main"
        },
        "Processed Invoice": {
          value: processedList.length + validatedList.length + activatedList.length + integratedList.length + validationErrorList.length + activationErrorList.length,
          section: "Main"
        },
        Validated: {
          value: validatedList.length + activatedList.length + integratedList.length + activationErrorList.length,
          section: "Main"
        },
        Activated: {
          value: activatedList.length + integratedList.length,
          section: "Main"
        },
        Integrated: {
          value: integratedList.length,
          section: "Main"
        },
        "Duplicate Invoice": {
          value: combinedArray.filter(
            (item) => item.Status === "Duplicate Invoice",
          ).length,
          section: "Exception"
        },
        "Not Processed": {
          value: notProcessedList.length,
          items: notProcessedList,
          expandable: true,
          section: "Exception"
        },
        "Validation Error": {
          value: validationErrorList.length,
          items: validationErrorList,
          expandable: true,
          section: "Exception"
        },
        "Activation Error": {
          value: activationErrorList.length,
          items: activationErrorList,
          expandable: true,
          section: "Exception"
        },
      };

      if (getIsMounted()) {
        dispatch(setInboxData({
          data: combinedArray,
          status: status,
        }));
      }
    } catch (error) {
      console.log(error);
    } finally {
      if (getIsMounted()) setLoading(false);
    }
  };

  const isPopoverOpen = Boolean(anchorEl);
  const popoverItem = popoverKey ? statusData[popoverKey] : null;
  const popoverConfig = popoverKey ? STATUS_CONFIG[popoverKey] : null;

  return (
    <Card sx={{
      height: open ? "85vh" : 500,
      minHeight: open ? "85vh" : 500,
      borderRadius: 4,
      boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
      border: "1px solid #f1f5f9",
      transition: "box-shadow 0.3s ease",
      display: "flex",
      flexDirection: "column",
      "&:hover": {
        boxShadow: "0 10px 30px rgba(0,0,0,0.08)"
      }
    }}>
      <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", p: 3, overflow: "hidden" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#475569", fontSize: "1.05rem", display: "flex", alignItems: "center", gap: 1.2 }}>
            <Box sx={{ p: 0.8, bgcolor: "#eef2ff", borderRadius: 2, display: "flex", boxShadow: "0 2px 8px rgba(99, 102, 241, 0.1)" }}>
              <MoveToInboxIcon sx={{ color: "#6366f1", fontSize: "1.1rem" }} />
            </Box>
            Inbox Summary
          </Typography>
          {!open && <IconButton onClick={() => setOpen((prev) => ({ ...prev, inbox: !prev.inbox }))} sx={{ bgcolor: "#f8fafc" }}><OpenInFullIcon sx={{ fontSize: "1rem" }} /></IconButton>}
        </Box>
        <Box sx={{ mb: 2.2 }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Select Month"
              value={period}
              views={["year", "month"]}
              onChange={() => { }}
              onAccept={(newValue) => updatePeriod(newValue)}
              renderInput={(params) => (
                <TextField
                  size="small"
                  fullWidth
                  {...params}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      bgcolor: "#f8fafc",
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "#e2e8f0" },
                      "&.Mui-focused fieldset": { borderColor: "#6366f1" },
                    },
                    "& .MuiInputLabel-root": { fontWeight: 600, color: "#475569", fontSize: "0.82rem" }
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            pr: 0.5,
            mr: -0.5,
            "&::-webkit-scrollbar": { width: "5px" },
            "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: "3px" },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" }
          }}
        >
          {loading ? (
            <Grid container spacing={1}>
              {[1, 2].map((col) => (
                <Grid item xs={12} md={6} key={col}>
                  <Skeleton width="60%" height={20} sx={{ mb: 1.5, ml: 1.5 }} />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} variant="rounded" height={45} sx={{ borderRadius: 2, mx: 1.5 }} />
                    ))}
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : statusData && Object.keys(statusData).length > 0 ? (
            <Grid container spacing={1}>
              {/* Left Column: Main Workflow Status */}
              <Grid item xs={12} md={6}>
                <Typography variant="overline" sx={{
                  color: "#475569",
                  fontWeight: 800,
                  letterSpacing: "1px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                  mb: 1.2,
                  px: 1.5,
                  fontSize: open ? "0.85rem" : "0.75rem"
                }}>
                  <LayersIcon sx={{ fontSize: open ? "1.1rem" : "0.8rem" }} />
                  Processing Status
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {Object.entries(statusData).filter(([_, item]) => item.section === "Main").map(([key, item], index) => (
                    <StatusRow
                      key={index}
                      item={item}
                      config={STATUS_CONFIG[key]}
                      isExpandable={item.expandable}
                      onClick={(e) => handlePopoverOpen(e, key)}
                      isDialogOpen={open}
                    />
                  ))}
                </Box>
              </Grid>

              {/* Right Column: Execution Exceptions */}
              <Grid item xs={12} md={6}>
                <Typography variant="overline" sx={{
                  color: "#475569",
                  fontWeight: 950,
                  letterSpacing: "1px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                  mb: 1.2,
                  px: 1.5,
                  fontSize: open ? "0.85rem" : "0.65rem"
                }}>
                  <WarningAmberIcon sx={{ fontSize: open ? "1.1rem" : "0.8rem" }} />
                  Execution Exceptions
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {Object.entries(statusData).filter(([_, item]) => item.section === "Exception").map(([key, item], index) => (
                    <StatusRow
                      key={index}
                      item={item}
                      config={STATUS_CONFIG[key]}
                      isExpandable={item.expandable}
                      onClick={(e) => handlePopoverOpen(e, key)}
                      isDialogOpen={open}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#94a3b8",
                gap: 2
              }}
            >
              <PieChartOutlineIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                No active counts available
              </Typography>
            </Box>
          )}
        </Box>

        {/* Failure Drill-down Popover */}
        <Popover
          open={isPopoverOpen}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
          transformOrigin={{ vertical: 'center', horizontal: 'center' }}
          PaperProps={{
            sx: {
              width: 420,
              maxHeight: 450,
              borderRadius: 4,
              boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
              border: "1px solid #f1f5f9",
              display: "flex",
              flexDirection: "column",
              p: 0,
              overflow: "hidden"
            }
          }}
        >
          <Box sx={{ p: 2.5, bgcolor: "#f8fafc", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{
                p: 0.8,
                borderRadius: 2,
                bgcolor: (popoverConfig?.color || "#475569") + "15",
                color: popoverConfig?.color || "#475569",
                display: "flex"
              }}>
                {popoverConfig?.icon && <popoverConfig.icon />}
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: "#475569" }}>
                Details: {popoverConfig?.label}
              </Typography>
            </Box>
            <IconButton size="small" onClick={handlePopoverClose}><CloseIcon fontSize="small" /></IconButton>
          </Box>
          <Divider />
          <Box sx={{ flexGrow: 1, overflowY: "auto", p: 1.5 }}>
            {popoverItem?.items && popoverItem.items.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {popoverItem.items.slice(0, 50).map((invoice, idx) => (
                  <Box key={idx} sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1.2,
                    borderRadius: 2,
                    transition: "0.2s",
                    "&:hover": { bgcolor: "#f1f5f9" }
                  }}>
                    <Box
                      onClick={() => handleBill(invoice)}
                      sx={{
                        color: "#475569",
                        fontWeight: 700,
                        fontSize: "0.80rem",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        textDecoration: "underline",
                        "&:hover": { color: "#1d4ed8" }
                      }}
                    >
                      <KeyboardArrowRightIcon sx={{ fontSize: "1rem" }} />
                      {invoice.BillNumber || invoice.Attachment.slice(0, 15) + "..."}
                    </Box>
                    <Typography variant="caption" sx={{
                      color: "#475569",
                      fontWeight: 500,
                      fontSize: "0.72rem",
                      fontStyle: "italic",
                      maxWidth: "60%",
                      textAlign: "right"
                    }}>
                      {invoice.ReasonForFailure || "Failure reason not logged"}
                    </Typography>
                  </Box>
                ))}
                {popoverItem.items.length > 50 && (
                  <Typography variant="caption" sx={{ color: "#475569", alignSelf: "center", mt: 1, fontWeight: 600 }}>
                    + {popoverItem.items.length - 50} more records
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ p: 4, textAlign: "center", color: "#94a3b8" }}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>No list available for {popoverConfig?.label}</Typography>
              </Box>
            )}
          </Box>
        </Popover>
      </CardContent>
    </Card >
  );
}

export default InboxDashboard;
